import api from '../core/api';

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open: () => void };
  }
}

export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

interface RazorpayCheckoutParams {
  amount: number;
  referenceType: string;
  referenceId: string;
  referenceNumber?: string;
  onSuccess?: (payment: unknown) => void;
  onFailure?: (error: unknown) => void;
}

export async function initiateRazorpayCheckout(params: RazorpayCheckoutParams) {
  const loaded = await loadRazorpayScript();
  if (!loaded) throw new Error('Failed to load Razorpay');

  const orderRes = await api.post('/payments/razorpay/create-order', {
    amount: params.amount,
    referenceType: params.referenceType,
    referenceId: params.referenceId,
    referenceNumber: params.referenceNumber,
  });

  const { keyId, orderId, amount, currency, sandbox } = orderRes.data.data;

  return new Promise((resolve, reject) => {
    const options = {
      key: keyId,
      amount: Math.round(amount * 100),
      currency,
      name: 'Super SaaS',
      description: params.referenceNumber || 'Payment',
      order_id: orderId,
      handler: async (response: {
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
      }) => {
        try {
          const verifyRes = await api.post('/payments/razorpay/verify', response);
          params.onSuccess?.(verifyRes.data.data);
          resolve(verifyRes.data.data);
        } catch (err) {
          params.onFailure?.(err);
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
      theme: { color: '#1976d2' },
      notes: { sandbox: sandbox ? 'true' : 'false' },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  });
}

export async function getPaymentGateways() {
  const res = await api.get('/payments/gateways');
  return res.data.data;
}

export async function updatePaymentGateway(type: string, data: Record<string, unknown>) {
  const res = await api.put(`/payments/gateways/${type}`, data);
  return res.data.data;
}

export async function createStripePaymentIntent(params: {
  amount: number;
  referenceType: string;
  referenceId: string;
  referenceNumber?: string;
}) {
  const res = await api.post('/payments/stripe/create-intent', params);
  return res.data.data;
}

export async function confirmStripePayment(paymentIntentId: string) {
  const res = await api.post('/payments/stripe/confirm', { paymentIntentId });
  return res.data.data;
}

export function loadStripeScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if ((window as unknown as { Stripe?: unknown }).Stripe) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export async function initiateStripeCheckout(params: {
  amount: number;
  referenceType: string;
  referenceId: string;
  referenceNumber?: string;
  onSuccess?: (payment: unknown) => void;
  onFailure?: (error: unknown) => void;
}) {
  const loaded = await loadStripeScript();
  if (!loaded) throw new Error('Failed to load Stripe.js');

  const intent = await createStripePaymentIntent(params);
  const Stripe = (window as unknown as { Stripe: (key: string) => {
    confirmCardPayment: (clientSecret: string) => Promise<{ paymentIntent?: { status: string }; error?: unknown }>;
  } }).Stripe;

  const stripe = Stripe(intent.publishableKey || intent.keyId);
  const result = await stripe.confirmCardPayment(intent.clientSecret);

  if (result.error) {
    params.onFailure?.(result.error);
    throw result.error;
  }

  if (result.paymentIntent?.status === 'succeeded') {
    const confirmed = await confirmStripePayment(intent.paymentIntentId);
    params.onSuccess?.(confirmed);
    return confirmed;
  }

  throw new Error('Payment not completed');
}
